/**
 * dagLayout.js — Production-grade Sugiyama layered DAG layout engine.
 *
 * Implements the full Sugiyama framework for drawing directed acyclic graphs:
 *   Phase 1: Cycle removal (greedy heuristic)
 *   Phase 2: Layer assignment (longest-path from sources)
 *   Phase 3: Dummy node insertion for long edges (span > 1 layer)
 *   Phase 4: Crossing minimization (multi-pass barycenter with transpose)
 *   Phase 5: Coordinate assignment (priority-based / Brandes-Köpf inspired)
 *
 * This replaces the hand-rolled layout that was missing phases 3 and 5,
 * resulting in edge crossings and poor node positioning.
 *
 * Usage:
 *   import { layoutDAG } from '../utils/dagLayout';
 *   const positioned = layoutDAG(nodes, connections, { nodeWidth: 200, nodeHeight: 72 });
 *   // positioned = [{ ...node, x, y }, ...]
 */

// ═══════════════════════════════════════════════════════════════════════════
// PUBLIC API
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Compute a layered DAG layout for the given nodes and connections.
 *
 * @param {Array<{id: string, type?: string}>} nodes
 * @param {Array<{from: string, to: string, fromPort?: string}>} connections
 * @param {Object} opts
 * @param {number} [opts.nodeWidth=200]   Width of each node box
 * @param {number} [opts.nodeHeight=72]   Height of each node box
 * @param {number} [opts.rankSep=140]     Vertical gap between layers
 * @param {number} [opts.nodeSep=60]      Horizontal gap between nodes in same layer
 * @param {number} [opts.padding=80]      Padding around the entire layout
 * @param {string} [opts.rankDir='TB']    Direction: 'TB' (top-bottom) or 'LR' (left-right)
 * @returns {Array<{id: string, x: number, y: number, ...rest}>}
 */
export function layoutDAG(nodes, connections, opts = {}) {
  const {
    nodeWidth = 200,
    nodeHeight = 72,
    rankSep = 140,
    nodeSep = 60,
    padding = 80,
    rankDir = 'TB',
    labelBudget = 60,   // extra horizontal space per-column on layers that
                        // receive labeled (non-default) edges — prevents
                        // SWITCH-case pill labels from colliding with siblings
  } = opts;

  if (!nodes || nodes.length === 0) return [];

  // Build graph representation
  const graph = buildGraph(nodes, connections);

  // Phase 1: Remove cycles (reverse back-edges)
  const { reversedEdges } = removeCycles(graph);

  // Phase 2: Layer assignment (longest-path from sources)
  const layers = assignLayers(graph);

  // Phase 3: Insert dummy nodes for long edges
  const { dummyNodes, dummyEdges, originalEdges, dummyChains } = insertDummyNodes(graph, layers);

  // Build the augmented graph with dummy nodes
  const augGraph = buildAugmentedGraph(graph, layers, dummyNodes, dummyEdges, originalEdges);

  // Phase 4: Crossing minimization (multi-pass barycenter + adjacent swap)
  const orderedLayers = minimizeCrossings(augGraph);

  // Identify layers that receive labeled (non-default) edges. Those layers
  // need extra horizontal breathing room so the edge label pills (rendered at
  // t=0.25 on the curve) don't overlap adjacent siblings.
  const labeledLayers = new Set();
  for (const edge of graph.edges) {
    const port = edge.fromPort || 'default';
    if (port === 'default') continue;
    const toLayer = layers.get(edge.to);
    if (toLayer != null) labeledLayers.add(toLayer);
  }

  // Phase 5: Coordinate assignment (priority method, centering children under parents)
  const coords = assignCoordinates(augGraph, orderedLayers, {
    nodeWidth, nodeHeight, rankSep, nodeSep, rankDir, labelBudget, labeledLayers,
  });

  // Phase 5.5: Apply ORKES DAG rules post-pass (layer pitch, trunk pinning, fork/switch splay, etc.)
  applyOrkesRules(nodes, connections, coords, nodeHeight, rankSep);

  // Map coordinates back to original nodes (skip dummy nodes)
  let minX = Infinity, minY = Infinity;
  const posMap = new Map();
  const dummyPosMap = new Map();
  for (const [nodeId, { x, y }] of coords) {
    if (nodeId.startsWith('__dummy_')) {
      dummyPosMap.set(nodeId, { x, y });
      // Still include in bounds so dummies influence offset calc, keeping
      // the whole layout in positive space when long edges exist.
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      continue;
    }
    posMap.set(nodeId, { x, y });
    if (x < minX) minX = x;
    if (y < minY) minY = y;
  }

  // Shift to positive space with padding
  const offsetX = padding - (Number.isFinite(minX) ? minX : 0);
  const offsetY = padding - (Number.isFinite(minY) ? minY : 0);

  // Compute bend points for long edges (span > 1 layer) using the dummy chain
  // coordinates. Edge renderer uses `bendPoints` (ELK-shaped: first and last
  // entries are node-border attach points, interior entries are waypoints).
  //
  // NOTE: enabling this produces piecewise-linear zigzags instead of clean
  // diagonals, which can look *worse* than an uncluttered diagonal on short
  // spans. We expose the map on the returned array but require callers to opt
  // in (e.g., via `window.localStorage.canvas.longEdgeBends`). Straight
  // diagonals remain the default once trunk anchoring (Phase 1b) is in.
  const bendPointsMap = new Map(); // "from__to" → [{x,y}, ...]
  if (dummyChains && dummyChains.length > 0) {
    for (const chain of dummyChains) {
      const fromPos = posMap.get(chain.from);
      const toPos = posMap.get(chain.to);
      if (!fromPos || !toPos) continue;

      const interior = [];
      for (const dummyId of chain.dummies) {
        const pos = dummyPosMap.get(dummyId);
        if (pos) {
          interior.push({
            x: pos.x + offsetX + nodeWidth / 2,
            y: pos.y + offsetY + nodeHeight / 2,
          });
        }
      }
      if (interior.length === 0) continue;

      const bendPoints = [
        { x: fromPos.x + offsetX + nodeWidth / 2, y: fromPos.y + offsetY + nodeHeight },
        ...interior,
        { x: toPos.x + offsetX + nodeWidth / 2, y: toPos.y + offsetY },
      ];
      bendPointsMap.set(`${chain.from}__${chain.to}`, bendPoints);
    }
  }

  const positionedNodes = nodes.map((n) => {
    const pos = posMap.get(n.id);
    if (pos) {
      const shifted = { ...n, x: pos.x + offsetX, y: pos.y + offsetY };
      // §121 DO_WHILE container bounds: translate the pre-offset container
      // bbox into the final positive-space coordinate system so CanvasPage's
      // renderer can use the bounds directly without another transform.
      if (n._containerBounds) {
        shifted._containerBounds = {
          x: n._containerBounds.x + offsetX,
          y: n._containerBounds.y + offsetY,
          width: n._containerBounds.width,
          height: n._containerBounds.height,
        };
      }
      return shifted;
    }
    // Fallback for nodes not in the graph (shouldn't happen)
    return { ...n, x: padding, y: padding };
  });

  // Attach bend points as a non-enumerable property on the returned array so
  // callers that don't know about them keep working (old callers just iterate
  // nodes), and callers that DO want them can pull `.__bendPoints`.
  Object.defineProperty(positionedNodes, '__bendPoints', {
    value: bendPointsMap,
    enumerable: false,
  });

  return positionedNodes;
}

/**
 * Validates Canvas JSON structure and returns issues + metrics.
 * Can be used for the JSON-first validation step before rendering.
 */
export function validateCanvasJSON(canvasData) {
  const issues = [];
  const metrics = {};
  const nodes = canvasData?.nodes || [];
  const connections = canvasData?.connections || [];

  metrics.totalNodes = nodes.length;
  metrics.totalConnections = connections.length;

  // Check for node IDs
  const nodeIds = new Set();
  const positions = new Map();
  let startNode = null, endNode = null;

  for (const node of nodes) {
    if (!node.id) {
      issues.push({ level: 'ERROR', message: 'Node without ID found' });
      continue;
    }
    if (nodeIds.has(node.id)) {
      issues.push({ level: 'ERROR', message: `Duplicate node ID: ${node.id}` });
    }
    nodeIds.add(node.id);

    if (node.type === 'manual_start') startNode = node.id;
    if (node.type === 'end') endNode = node.id;

    // Check overlapping positions
    const posKey = `${node.x},${node.y}`;
    if (positions.has(posKey)) {
      issues.push({
        level: 'WARN',
        message: `Overlapping positions at (${node.x},${node.y}): ${positions.get(posKey)} and ${node.id}`,
      });
    }
    positions.set(posKey, node.id);
  }

  if (!startNode) issues.push({ level: 'ERROR', message: 'No start node found' });
  if (!endNode) issues.push({ level: 'ERROR', message: 'No end node found' });

  // Check connections
  const nodesWithIncoming = new Set();
  const nodesWithOutgoing = new Set();

  for (const conn of connections) {
    if (!conn.from || !conn.to) {
      issues.push({ level: 'ERROR', message: `Connection ${conn.id} missing from/to` });
      continue;
    }
    if (!nodeIds.has(conn.from)) {
      issues.push({ level: 'ERROR', message: `Connection ${conn.id} references unknown source: ${conn.from}` });
    }
    if (!nodeIds.has(conn.to)) {
      issues.push({ level: 'ERROR', message: `Connection ${conn.id} references unknown target: ${conn.to}` });
    }
    if (conn.from === conn.to) {
      issues.push({ level: 'WARN', message: `Self-loop on node: ${conn.from}` });
    }
    nodesWithIncoming.add(conn.to);
    nodesWithOutgoing.add(conn.from);
  }

  // Reachability checks
  for (const nodeId of nodeIds) {
    if (nodeId === startNode || nodeId === endNode) continue;
    if (!nodesWithIncoming.has(nodeId)) {
      issues.push({ level: 'WARN', message: `Unreachable node: ${nodeId}` });
    }
  }

  if (startNode && !nodesWithOutgoing.has(startNode)) {
    issues.push({ level: 'ERROR', message: 'Start node has no outgoing connections' });
  }
  if (endNode && !nodesWithIncoming.has(endNode)) {
    issues.push({ level: 'ERROR', message: 'End node has no incoming connections' });
  }

  // Graph metrics
  const layerCounts = computeLayerCounts(nodes, connections);
  metrics.layers = layerCounts.length;
  metrics.maxLayerWidth = layerCounts.length > 0 ? Math.max(...layerCounts) : 0;
  metrics.avgLayerWidth = layerCounts.length > 0
    ? (layerCounts.reduce((a, b) => a + b, 0) / layerCounts.length).toFixed(1)
    : 0;

  return {
    valid: issues.filter((i) => i.level === 'ERROR').length === 0,
    issues,
    metrics,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// INTERNAL: GRAPH REPRESENTATION
// ═══════════════════════════════════════════════════════════════════════════

function buildGraph(nodes, connections) {
  const nodeSet = new Set(nodes.map((n) => n.id));
  const adj = new Map();     // from → [to, ...]
  const radj = new Map();    // to → [from, ...]
  const edges = [];

  nodeSet.forEach((id) => {
    adj.set(id, []);
    radj.set(id, []);
  });

  for (const c of connections) {
    if (nodeSet.has(c.from) && nodeSet.has(c.to) && c.from !== c.to) {
      adj.get(c.from).push(c.to);
      radj.get(c.to).push(c.from);
      edges.push({ from: c.from, to: c.to, fromPort: c.fromPort || 'default' });
    }
  }

  return { nodeSet, adj, radj, edges };
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 1: CYCLE REMOVAL (Greedy DFS)
// ═══════════════════════════════════════════════════════════════════════════

function removeCycles(graph) {
  const { nodeSet, adj } = graph;
  const visited = new Set();
  const inStack = new Set();
  const reversedEdges = [];

  function dfs(node) {
    visited.add(node);
    inStack.add(node);
    const neighbors = adj.get(node) || [];
    for (let i = 0; i < neighbors.length; i++) {
      const next = neighbors[i];
      if (inStack.has(next)) {
        // Back-edge detected — reverse it
        neighbors.splice(i, 1);
        i--;
        const reverseAdj = adj.get(next) || [];
        // Don't add reverse if it creates a multi-edge
        if (!reverseAdj.includes(node)) {
          reverseAdj.push(node);
        }
        // Update radj
        const rNext = graph.radj.get(next) || [];
        const idx = rNext.indexOf(node);
        if (idx >= 0) rNext.splice(idx, 1);
        const rNode = graph.radj.get(node) || [];
        if (!rNode.includes(next)) rNode.push(next);

        reversedEdges.push({ from: node, to: next });
      } else if (!visited.has(next)) {
        dfs(next);
      }
    }
    inStack.delete(node);
  }

  for (const node of nodeSet) {
    if (!visited.has(node)) dfs(node);
  }

  return { reversedEdges };
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 2: LAYER ASSIGNMENT (Longest-Path from Sources)
// ═══════════════════════════════════════════════════════════════════════════

function assignLayers(graph) {
  const { nodeSet, adj, radj } = graph;
  const layers = new Map();

  // Find source nodes (no incoming edges)
  const sources = [];
  for (const node of nodeSet) {
    if ((radj.get(node) || []).length === 0) sources.push(node);
  }

  // BFS longest-path assignment
  // Initialize all to -1
  for (const node of nodeSet) layers.set(node, -1);

  // Topological sort using Kahn's algorithm
  const inDegree = new Map();
  for (const node of nodeSet) {
    inDegree.set(node, (radj.get(node) || []).length);
  }

  const queue = [...sources];
  const topoOrder = [];

  // If no sources found (fully cyclic after removal), pick the first node
  if (queue.length === 0 && nodeSet.size > 0) {
    const first = nodeSet.values().next().value;
    queue.push(first);
    inDegree.set(first, 0);
  }

  while (queue.length > 0) {
    const node = queue.shift();
    topoOrder.push(node);
    for (const next of (adj.get(node) || [])) {
      inDegree.set(next, inDegree.get(next) - 1);
      if (inDegree.get(next) === 0) {
        queue.push(next);
      }
    }
  }

  // Handle nodes not reached by topo sort (shouldn't happen after cycle removal)
  for (const node of nodeSet) {
    if (!topoOrder.includes(node)) topoOrder.push(node);
  }

  // Assign layers: longest path from any source
  for (const node of topoOrder) {
    const parents = radj.get(node) || [];
    if (parents.length === 0) {
      layers.set(node, 0);
    } else {
      let maxParentLayer = 0;
      for (const p of parents) {
        const pLayer = layers.get(p);
        if (pLayer >= 0) {
          maxParentLayer = Math.max(maxParentLayer, pLayer + 1);
        }
      }
      layers.set(node, maxParentLayer);
    }
  }

  // P3-A Bug #2: Fix orphan subtrees by computing depth of branch chains
  // For nodes with multiple parents (reconvergences), ensure all parents
  // are at consistent layers relative to their branch depth.
  // Walk the graph one more time to fix any orphan nodes left at layer 0
  // that should be deeper based on their ancestry chain depth.
  let changed = true;
  let iterations = 0;
  while (changed && iterations < 10) {
    iterations++;
    changed = false;
    for (const node of topoOrder) {
      const parents = radj.get(node) || [];
      if (parents.length > 0) {
        let maxParentLayer = 0;
        for (const p of parents) {
          const pLayer = layers.get(p) ?? 0;
          maxParentLayer = Math.max(maxParentLayer, pLayer + 1);
        }
        const currentLayer = layers.get(node) ?? 0;
        if (maxParentLayer > currentLayer) {
          layers.set(node, maxParentLayer);
          changed = true;
        }
      }
    }
  }

  // Fix any remaining -1 layers
  for (const node of nodeSet) {
    if (layers.get(node) < 0) layers.set(node, 0);
  }

  return layers;
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 3: DUMMY NODE INSERTION
// For edges spanning more than 1 layer, insert dummy nodes so every edge
// only connects adjacent layers. This is CRITICAL for clean routing.
// ═══════════════════════════════════════════════════════════════════════════

function insertDummyNodes(graph, layers) {
  const dummyNodes = []; // { id, layer }
  const dummyEdges = []; // { from, to } — new edges through dummy chain
  const originalEdges = new Set(); // edges to remove (replaced by dummy chains)
  const dummyChains = []; // { from, to, dummies: [id...] } — for bend-point recovery
  let dummyCount = 0;

  for (const edge of graph.edges) {
    const fromLayer = layers.get(edge.from);
    const toLayer = layers.get(edge.to);
    const span = toLayer - fromLayer;

    if (span > 1) {
      // This edge spans multiple layers — insert dummy nodes
      originalEdges.add(`${edge.from}->${edge.to}`);
      let prev = edge.from;
      const chainDummies = [];

      for (let l = fromLayer + 1; l < toLayer; l++) {
        const dummyId = `__dummy_${dummyCount++}`;
        dummyNodes.push({ id: dummyId, layer: l, isLongEdge: true, originalFrom: edge.from, originalTo: edge.to });
        dummyEdges.push({ from: prev, to: dummyId });
        chainDummies.push(dummyId);
        prev = dummyId;
      }
      // Final edge from last dummy to target
      dummyEdges.push({ from: prev, to: edge.to });
      dummyChains.push({ from: edge.from, to: edge.to, dummies: chainDummies });
    }
  }

  return { dummyNodes, dummyEdges, originalEdges, dummyChains };
}

// Build the augmented graph with dummy nodes for crossing minimization
function buildAugmentedGraph(graph, layers, dummyNodes, dummyEdges, originalEdges) {
  // Collect all node IDs and their layers
  const allNodes = new Map(); // nodeId → { layer, isDummy }
  for (const [nodeId, layer] of layers) {
    allNodes.set(nodeId, { layer, isDummy: false });
  }
  for (const d of dummyNodes) {
    allNodes.set(d.id, { layer: d.layer, isDummy: true });
  }

  // Build adjacency including dummy edges
  const adj = new Map();
  const radj = new Map();
  for (const [id] of allNodes) {
    adj.set(id, []);
    radj.set(id, []);
  }

  // Add original edges (excluding those replaced by dummy chains)
  for (const edge of graph.edges) {
    const key = `${edge.from}->${edge.to}`;
    if (!originalEdges.has(key)) {
      adj.get(edge.from).push(edge.to);
      radj.get(edge.to).push(edge.from);
    }
  }

  // Add dummy edges
  for (const edge of dummyEdges) {
    if (adj.has(edge.from) && adj.has(edge.to)) {
      adj.get(edge.from).push(edge.to);
      radj.get(edge.to).push(edge.from);
    }
  }

  // Build layer → [nodeIds] mapping
  const layerNodes = new Map(); // layer → [nodeId, ...]
  let maxLayer = 0;
  for (const [nodeId, info] of allNodes) {
    const l = info.layer;
    if (!layerNodes.has(l)) layerNodes.set(l, []);
    layerNodes.get(l).push(nodeId);
    if (l > maxLayer) maxLayer = l;
  }

  return { allNodes, adj, radj, layerNodes, maxLayer };
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 4: CROSSING MINIMIZATION (Barycenter + Transpose)
// Multi-pass alternating down/up sweep with adjacent-swap improvement.
// ═══════════════════════════════════════════════════════════════════════════

function minimizeCrossings(augGraph) {
  const { allNodes, adj, radj, layerNodes, maxLayer } = augGraph;

  // Initialize layer order arrays
  const order = new Map(); // layer → [nodeId, ...]
  for (let l = 0; l <= maxLayer; l++) {
    order.set(l, [...(layerNodes.get(l) || [])]);
  }

  // Position lookup: nodeId → index within its layer
  const posOf = new Map();
  function updatePositions() {
    for (let l = 0; l <= maxLayer; l++) {
      const row = order.get(l) || [];
      row.forEach((id, idx) => posOf.set(id, idx));
    }
  }
  updatePositions();

  // Barycenter for a node based on neighbors in a fixed layer
  function barycenter(nodeId, neighbors) {
    if (neighbors.length === 0) return posOf.get(nodeId) ?? 0;
    let sum = 0;
    for (const n of neighbors) {
      sum += posOf.get(n) ?? 0;
    }
    return sum / neighbors.length;
  }

  // Count crossings between two adjacent layers
  function countCrossings(upperLayer, lowerLayer) {
    const upper = order.get(upperLayer) || [];
    const lower = order.get(lowerLayer) || [];
    if (upper.length === 0 || lower.length === 0) return 0;

    // Collect edges between these layers as (upperPos, lowerPos) pairs
    const edgePairs = [];
    upper.forEach((u, ui) => {
      const children = (adj.get(u) || []).filter((c) => {
        const info = allNodes.get(c);
        return info && info.layer === lowerLayer;
      });
      children.forEach((c) => {
        const ci = lower.indexOf(c);
        if (ci >= 0) edgePairs.push([ui, ci]);
      });
    });

    // Count inversions (crossings)
    let crossings = 0;
    for (let i = 0; i < edgePairs.length; i++) {
      for (let j = i + 1; j < edgePairs.length; j++) {
        if ((edgePairs[i][0] - edgePairs[j][0]) * (edgePairs[i][1] - edgePairs[j][1]) < 0) {
          crossings++;
        }
      }
    }
    return crossings;
  }

  // Run multiple down/up sweeps
  const NUM_SWEEPS = 24;
  let bestCrossings = Infinity;
  let bestOrder = null;

  // Save current order
  function cloneOrder() {
    const clone = new Map();
    for (let l = 0; l <= maxLayer; l++) {
      clone.set(l, [...(order.get(l) || [])]);
    }
    return clone;
  }

  function totalCrossings() {
    let total = 0;
    for (let l = 0; l < maxLayer; l++) {
      total += countCrossings(l, l + 1);
    }
    return total;
  }

  // Initial crossing count
  bestCrossings = totalCrossings();
  bestOrder = cloneOrder();

  for (let sweep = 0; sweep < NUM_SWEEPS; sweep++) {
    // Down sweep: fix upper layer, reorder lower by barycenter
    for (let l = 1; l <= maxLayer; l++) {
      const row = order.get(l) || [];
      row.sort((a, b) => {
        const parentsA = (radj.get(a) || []).filter((p) => {
          const info = allNodes.get(p);
          return info && info.layer === l - 1;
        });
        const parentsB = (radj.get(b) || []).filter((p) => {
          const info = allNodes.get(p);
          return info && info.layer === l - 1;
        });
        return barycenter(a, parentsA) - barycenter(b, parentsB);
      });
      order.set(l, row);
      updatePositions();
    }

    // Up sweep: fix lower layer, reorder upper by barycenter
    for (let l = maxLayer - 1; l >= 0; l--) {
      const row = order.get(l) || [];
      row.sort((a, b) => {
        const childrenA = (adj.get(a) || []).filter((c) => {
          const info = allNodes.get(c);
          return info && info.layer === l + 1;
        });
        const childrenB = (adj.get(b) || []).filter((c) => {
          const info = allNodes.get(c);
          return info && info.layer === l + 1;
        });
        return barycenter(a, childrenA) - barycenter(b, childrenB);
      });
      order.set(l, row);
      updatePositions();
    }

    // Adjacent swap (transpose) improvement on each layer
    for (let l = 0; l <= maxLayer; l++) {
      let improved = true;
      while (improved) {
        improved = false;
        const row = order.get(l);
        for (let i = 0; i < row.length - 1; i++) {
          // Count crossings before swap
          const prevCross = (l > 0 ? countCrossings(l - 1, l) : 0)
                          + (l < maxLayer ? countCrossings(l, l + 1) : 0);

          // Swap
          [row[i], row[i + 1]] = [row[i + 1], row[i]];
          updatePositions();

          const afterCross = (l > 0 ? countCrossings(l - 1, l) : 0)
                           + (l < maxLayer ? countCrossings(l, l + 1) : 0);

          if (afterCross < prevCross) {
            improved = true; // Keep the swap
          } else {
            // Revert
            [row[i], row[i + 1]] = [row[i + 1], row[i]];
            updatePositions();
          }
        }
      }
    }

    const tc = totalCrossings();
    if (tc < bestCrossings) {
      bestCrossings = tc;
      bestOrder = cloneOrder();
    }

    // Early termination if zero crossings
    if (bestCrossings === 0) break;
  }

  // Restore best order
  if (bestOrder) {
    for (let l = 0; l <= maxLayer; l++) {
      order.set(l, bestOrder.get(l) || []);
    }
  }

  return order;
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 5: COORDINATE ASSIGNMENT (Priority / Median centering)
// Centers each node under its parents/children for clean edge routing.
// ═══════════════════════════════════════════════════════════════════════════

function assignCoordinates(augGraph, orderedLayers, opts) {
  const { allNodes, adj, radj } = augGraph;
  const { nodeWidth, nodeHeight, rankSep, nodeSep, rankDir,
          labelBudget = 0, labeledLayers = new Set() } = opts;

  const maxLayer = augGraph.maxLayer;
  const cellWidth = nodeWidth + nodeSep;
  // Expanded cellWidth for layers that receive labeled edges — leaves room
  // for the SWITCH/FORK case-label pills on edges feeding this row.
  const labeledCellWidth = nodeWidth + nodeSep + labelBudget;
  const cellWidthFor = (l) => (labeledLayers.has(l) ? labeledCellWidth : cellWidth);

  // Identify the SPINE: every layer's unique "bottleneck" real node — i.e. the
  // sole real (non-dummy, non-terminated) node in that layer that has a forward
  // path to a sink. Bottleneck nodes form the visual main trunk a reader
  // follows top-to-bottom; anchoring them to a single X column prevents the
  // zig-zag drift that would otherwise result from medianing at every layer.
  //
  // We also seed with the longest-path spine so multi-node layers still
  // contribute an anchor when one of the candidates is clearly the main flow.
  const spineLongest = computeSpine(augGraph, orderedLayers);
  const spineBottlenecks = computeBottleneckSpine(augGraph, orderedLayers);
  const spineSet = new Set([...spineLongest, ...spineBottlenecks]);

  // Step 1: Initial placement — center each layer
  const coords = new Map(); // nodeId → { x, y }

  for (let l = 0; l <= maxLayer; l++) {
    const row = orderedLayers.get(l) || [];
    const cw = cellWidthFor(l);
    const totalWidth = row.length * cw - (row.length > 0 ? (cw - nodeWidth) : 0);
    const startX = -totalWidth / 2;

    row.forEach((nodeId, idx) => {
      const x = startX + idx * cw;
      const y = l * (nodeHeight + rankSep);
      coords.set(nodeId, { x, y });
    });
  }

  // Step 2: Iterative centering — push nodes toward median of neighbors
  // This is a simplified Brandes-Köpf: for each node, compute the median
  // x-position of its upper neighbors, and shift toward it if possible
  // without violating minimum separation.

  for (let iter = 0; iter < 8; iter++) {
    // Down pass: center nodes under parents
    for (let l = 1; l <= maxLayer; l++) {
      const row = orderedLayers.get(l) || [];
      const desiredX = [];

      for (const nodeId of row) {
        const parents = (radj.get(nodeId) || []).filter((p) => {
          const info = allNodes.get(p);
          return info && info.layer === l - 1;
        });
        if (parents.length > 0) {
          const parentXs = parents.map((p) => coords.get(p)?.x ?? 0).sort((a, b) => a - b);
          // Median
          const mid = Math.floor(parentXs.length / 2);
          const median = parentXs.length % 2 === 1
            ? parentXs[mid]
            : (parentXs[mid - 1] + parentXs[mid]) / 2;
          desiredX.push({ nodeId, x: median });
        } else {
          desiredX.push({ nodeId, x: coords.get(nodeId)?.x ?? 0 });
        }
      }

      // Apply desired positions while maintaining minimum separation
      applyDesiredPositions(row, desiredX, coords, cellWidthFor(l));
    }

    // Up pass: center nodes above children
    for (let l = maxLayer - 1; l >= 0; l--) {
      const row = orderedLayers.get(l) || [];
      const desiredX = [];

      for (const nodeId of row) {
        const children = (adj.get(nodeId) || []).filter((c) => {
          const info = allNodes.get(c);
          return info && info.layer === l + 1;
        });
        if (children.length > 0) {
          const childXs = children.map((c) => coords.get(c)?.x ?? 0).sort((a, b) => a - b);
          const mid = Math.floor(childXs.length / 2);
          const median = childXs.length % 2 === 1
            ? childXs[mid]
            : (childXs[mid - 1] + childXs[mid]) / 2;
          desiredX.push({ nodeId, x: median });
        } else {
          desiredX.push({ nodeId, x: coords.get(nodeId)?.x ?? 0 });
        }
      }

      applyDesiredPositions(row, desiredX, coords, cellWidthFor(l));
    }
  }

  // Step 2.5: Trunk anchoring. Compute the target X for the spine (median of
  // current spine Xs) and snap each spine node to it if free; otherwise
  // reposition by cascading through min-sep enforcement. Then run one final
  // pair of down/up passes so branch nodes re-center under the anchored spine.
  if (spineSet.size >= 3) {
    const spineXs = [];
    for (const id of spineSet) {
      const c = coords.get(id);
      if (c) spineXs.push(c.x);
    }
    if (spineXs.length > 0) {
      spineXs.sort((a, b) => a - b);
      const spineAnchorX = spineXs[Math.floor(spineXs.length / 2)];

      // Snap spine nodes to the anchor; reflow dummies and other non-spine
      // siblings around them while preserving min-sep. Unlike applyDesired-
      // Positions (which centers the whole row), here we FORCE spine nodes
      // to spineAnchorX — they are the trunk and may not drift — and pack
      // everything else around them.
      for (let l = 0; l <= maxLayer; l++) {
        const row = orderedLayers.get(l) || [];
        if (row.length === 0) continue;
        const cw = cellWidthFor(l);

        // Locate the spine node (if any) in this row
        const spineIdx = row.findIndex((id) => spineSet.has(id));
        if (spineIdx < 0) continue; // no spine node — leave row alone

        // Place spine node at the anchor, then pack others outward.
        const newX = new Array(row.length);
        newX[spineIdx] = spineAnchorX;
        for (let i = spineIdx - 1; i >= 0; i--) {
          newX[i] = Math.min(coords.get(row[i])?.x ?? (newX[i + 1] - cw), newX[i + 1] - cw);
        }
        for (let i = spineIdx + 1; i < row.length; i++) {
          newX[i] = Math.max(coords.get(row[i])?.x ?? (newX[i - 1] + cw), newX[i - 1] + cw);
        }
        for (let i = 0; i < row.length; i++) {
          const cur = coords.get(row[i]);
          if (cur) coords.set(row[i], { x: newX[i], y: cur.y });
        }
      }

      // One final centering pass for branches around the now-anchored spine.
      // Spine nodes stay pinned at spineAnchorX; non-spine siblings pack
      // around them via median-of-parents with min-sep enforcement.
      for (let l = 1; l <= maxLayer; l++) {
        const row = orderedLayers.get(l) || [];
        if (row.length === 0) continue;
        const cw = cellWidthFor(l);
        const spineIdx = row.findIndex((id) => spineSet.has(id));

        // Compute desired positions: spine → anchor, others → median of parents
        const newX = new Array(row.length);
        for (let i = 0; i < row.length; i++) {
          const nodeId = row[i];
          if (spineSet.has(nodeId)) {
            newX[i] = spineAnchorX;
            continue;
          }
          const parents = (radj.get(nodeId) || []).filter((p) => {
            const info = allNodes.get(p);
            return info && info.layer === l - 1;
          });
          if (parents.length > 0) {
            const parentXs = parents.map((p) => coords.get(p)?.x ?? 0).sort((a, b) => a - b);
            const mid = Math.floor(parentXs.length / 2);
            newX[i] = parentXs.length % 2 === 1
              ? parentXs[mid]
              : (parentXs[mid - 1] + parentXs[mid]) / 2;
          } else {
            newX[i] = coords.get(nodeId)?.x ?? 0;
          }
        }

        // Pack around spine: left-of-spine must be ≤ spine − cw (cascading),
        // right-of-spine ≥ spine + cw (cascading). Preserves spine anchor.
        if (spineIdx >= 0) {
          for (let i = spineIdx - 1; i >= 0; i--) {
            newX[i] = Math.min(newX[i], newX[i + 1] - cw);
          }
          for (let i = spineIdx + 1; i < row.length; i++) {
            newX[i] = Math.max(newX[i], newX[i - 1] + cw);
          }
        } else {
          // No spine node — standard left-to-right min-sep enforcement
          for (let i = 1; i < row.length; i++) {
            newX[i] = Math.max(newX[i], newX[i - 1] + cw);
          }
        }

        for (let i = 0; i < row.length; i++) {
          const cur = coords.get(row[i]);
          if (cur) coords.set(row[i], { x: newX[i], y: cur.y });
        }
      }
    }
  }

  // Step 3: If rankDir is LR, transpose x and y
  if (rankDir === 'LR') {
    for (const [nodeId, pos] of coords) {
      coords.set(nodeId, { x: pos.y, y: pos.x });
    }
  }

  return coords;
}

/**
 * Compute the "spine" of the DAG — the longest path from any source to any
 * sink, measured in layer count. Nodes on the spine form the main vertical
 * flow that must not drift horizontally.
 *
 * Uses a two-pass dynamic program on the augmented graph:
 *   1. Forward DP: longest path from any source to each node
 *   2. Backward DP: longest path from each node to any sink
 *   3. The spine is the max-sum path reconstructed by following the DP parent
 *      pointers from the overall max-weighted node.
 *
 * Dummy nodes are included (they contribute to path length) but stripped from
 * the returned spine set — only real nodes get anchored.
 */
function computeSpine(augGraph, orderedLayers) {
  const { allNodes, adj, radj } = augGraph;

  // Forward DP: depth = longest path FROM a source
  const depth = new Map();
  const forwardParent = new Map();
  const layerSorted = [];
  const maxL = augGraph.maxLayer;
  for (let l = 0; l <= maxL; l++) {
    for (const id of (orderedLayers.get(l) || [])) layerSorted.push(id);
  }
  for (const id of layerSorted) {
    const parents = (radj.get(id) || []).filter((p) => allNodes.has(p));
    if (parents.length === 0) {
      depth.set(id, 0);
    } else {
      let best = -1, bestP = null;
      for (const p of parents) {
        const d = (depth.get(p) ?? 0) + 1;
        if (d > best) { best = d; bestP = p; }
      }
      depth.set(id, best);
      if (bestP) forwardParent.set(id, bestP);
    }
  }

  // Find sink with maximum depth
  let maxNode = null, maxDepth = -1;
  for (const [id, d] of depth) {
    const children = (adj.get(id) || []).filter((c) => allNodes.has(c));
    if (children.length === 0 && d > maxDepth) {
      maxDepth = d;
      maxNode = id;
    }
  }
  // If no true sink (unlikely), take the node with max depth overall
  if (!maxNode) {
    for (const [id, d] of depth) {
      if (d > maxDepth) { maxDepth = d; maxNode = id; }
    }
  }

  // Walk backward from maxNode following forwardParent pointers
  const spine = [];
  let cursor = maxNode;
  const visited = new Set();
  while (cursor && !visited.has(cursor)) {
    visited.add(cursor);
    // Only include real nodes in the anchored spine — dummies float along
    if (!cursor.startsWith('__dummy_')) spine.push(cursor);
    cursor = forwardParent.get(cursor);
  }

  return spine;
}

/**
 * Compute the "bottleneck" spine — every layer whose sole real (non-dummy)
 * node has a forward path to a sink. These are the visual backbone nodes a
 * reader follows top-to-bottom regardless of which path happens to be the
 * longest. Dead-end terminate nodes and branch candidates are excluded
 * because they aren't visually on the trunk.
 *
 * This complements computeSpine (longest source→sink path). Together they
 * cover: (a) bottleneck nodes with a single real occupant per layer, and
 * (b) longest-path nodes where a layer has multiple real nodes but one is
 * clearly the main flow.
 */
function computeBottleneckSpine(augGraph, orderedLayers) {
  const { allNodes, adj, radj } = augGraph;
  const maxL = augGraph.maxLayer;

  // Only the DEEPEST-layer sinks count as "main end" nodes; any other leaf
  // (e.g. a TERMINATE branch ending early) is a dead-end that shouldn't pull
  // its layer onto the spine.
  const reachesSink = new Set();
  const topoReverse = [];
  for (let l = maxL; l >= 0; l--) {
    for (const id of (orderedLayers.get(l) || [])) topoReverse.push(id);
  }
  // Seed: every node whose own layer is the max layer AND has no outgoing
  // edges is considered a main sink. (Typically the __end__ node.)
  for (const id of (orderedLayers.get(maxL) || [])) {
    const children = (adj.get(id) || []).filter((c) => allNodes.has(c));
    if (children.length === 0) reachesSink.add(id);
  }
  for (const id of topoReverse) {
    if (reachesSink.has(id)) continue;
    const children = (adj.get(id) || []).filter((c) => allNodes.has(c));
    if (children.length === 0) continue; // dead-end leaf not at max-layer
    if (children.some((c) => reachesSink.has(c))) reachesSink.add(id);
  }

  const spine = [];
  for (let l = 0; l <= maxL; l++) {
    const row = (orderedLayers.get(l) || []);
    const realLive = row.filter((id) => !id.startsWith('__dummy_') && reachesSink.has(id));
    if (realLive.length === 1) spine.push(realLive[0]);
  }
  return spine;
}

/**
 * Given a row of node IDs (in order) and desired X positions,
 * apply positions while maintaining minimum separation (cellWidth).
 *
 * Key insight: when multiple nodes want the same position (e.g., siblings
 * of the same parent), we spread them CENTERED around the desired position
 * rather than pushing them all rightward. This prevents drift.
 */
function applyDesiredPositions(row, desiredX, coords, cellWidth) {
  if (row.length === 0) return;

  // Build a lookup from nodeId → desired x
  const desired = new Map();
  for (const d of desiredX) {
    desired.set(d.nodeId, d.x);
  }

  // Start with desired positions
  const newX = new Array(row.length);
  for (let i = 0; i < row.length; i++) {
    newX[i] = desired.get(row[i]) ?? coords.get(row[i])?.x ?? 0;
  }

  // Compute the centroid of desired positions BEFORE min-sep enforcement
  let sumDesired = 0;
  for (let i = 0; i < row.length; i++) sumDesired += newX[i];
  const centroidDesired = sumDesired / row.length;

  // Left-to-right sweep: ensure minimum separation
  for (let i = 1; i < row.length; i++) {
    const minX = newX[i - 1] + cellWidth;
    if (newX[i] < minX) {
      newX[i] = minX;
    }
  }

  // Right-to-left sweep: pull nodes back toward desired position
  for (let i = row.length - 2; i >= 0; i--) {
    const maxX = newX[i + 1] - cellWidth;
    if (newX[i] > maxX) {
      newX[i] = maxX;
    }
  }

  // Centering correction: shift the entire block so the centroid of
  // actual positions matches the centroid of desired positions.
  // This prevents the rightward drift caused by min-separation.
  let sumActual = 0;
  for (let i = 0; i < row.length; i++) sumActual += newX[i];
  const centroidActual = sumActual / row.length;
  const shift = centroidDesired - centroidActual;
  for (let i = 0; i < row.length; i++) {
    newX[i] += shift;
  }

  // Apply
  for (let i = 0; i < row.length; i++) {
    const current = coords.get(row[i]);
    if (current) {
      coords.set(row[i], { x: newX[i], y: current.y });
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 5.5: ORKES DAG RULES POST-PASS
// Applies layer pitch, trunk pinning, fork/switch splay, catch-pill positioning,
// DO_WHILE container sizing per ORKES_DAG_RULESET.md §0y–§0z.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Apply Conductor OSS-style DAG layout rules after Sugiyama coordinate assignment.
 * This post-pass:
 *   1. Enforces layer pitch: y_{n+1} = y_n + h_n + 70
 *   2. Pins trunk nodes to a single x-coordinate
 *   3. Splays fork/switch branches symmetrically (±278px)
 *   4. Positions catch pills at the x-centroid of live (non-TERMINATE) branches
 *   5. Sizes DO_WHILE containers dynamically from enclosed content bboxes
 *
 * @param {Array} nodes - original node list
 * @param {Array} connections - original connection list
 * @param {Map} coords - nodeId → {x, y} coordinate map from Sugiyama
 * @param {number} nodeHeight - default node height (used as fallback)
 * @param {number} rankSep - vertical gap parameter (not directly used, kept for reference)
 */
function applyOrkesRules(nodes, connections, coords, nodeHeight, rankSep) {
  if (!nodes || nodes.length === 0 || !coords) return;

  const nodeMap = new Map();
  nodes.forEach((n) => nodeMap.set(n.id, n));

  // Build adjacency for traversal
  const adj = new Map();
  const radj = new Map();
  nodes.forEach((n) => { adj.set(n.id, []); radj.set(n.id, []); });
  for (const c of connections) {
    if (nodeMap.has(c.from) && nodeMap.has(c.to)) {
      adj.get(c.from).push(c.to);
      radj.get(c.to).push(c.from);
    }
  }

  // §0y: Layer pitch rule — recompute y-coordinates top-down
  // y_{n+1} = y_n + h_n + 70 where h_n is node height (default 100).
  applyLayerPitch(nodes, coords, nodeMap, adj, radj, nodeHeight);

  // §0z.1: Trunk pinning — find longest linear path with no fan-out/fan-in
  // and pin all trunk nodes to a single x-coordinate.
  const trunkSet = computeTrunkPath(nodes, adj, radj);
  const trunkX = applyTrunkPinning(nodes, coords, trunkSet);

  // §0z.3: Switch case splay — for SWITCH with N cases + default,
  // default leftmost at trunk_x - (N) * 370/2, named cases extend rightward.
  //
  // CRITICAL: Switch splay must run BEFORE fork splay — the switch BFS places
  // every branch member (including any FORK nodes on that branch) at the
  // branch's local caseX. Fork splay then reads that corrected caseX as the
  // fork's own x and splays the fork's parallel children around IT, not
  // around the global trunk. Running fork splay first produced (x,y)
  // collisions whenever a FORK was nested inside a SWITCH case, because the
  // subsequent switch BFS would descend THROUGH the fork's children and
  // collapse both parallel branches onto the case column.
  applySwitchSplay(nodes, coords, adj, radj, trunkX);

  // §0z.2: Fork branch splay — for FORK_JOIN with N legs, position around
  // the fork's own x (not the global trunk) with pitch 278. Placing children
  // relative to the fork means nested FORKs inside SWITCH cases keep their
  // parallel branches on the local column instead of being pulled to x=0.
  applyForkSplay(nodes, coords, adj, trunkX);

  // §0z.4: Catch pill positioning — x = centroid of live (non-TERMINATE)
  // branches of each convergence node.
  applyCatchPillPositioning(nodes, coords, nodeMap, adj, radj);

  // §0z.5: Container sizing — dynamic frame from bbox + 50px horiz padding,
  // 30px bottom padding. Generalized (#141) from DO_WHILE-only to all five
  // container kinds: SWITCH, FORK_JOIN, FORK_JOIN_DYNAMIC, DO_WHILE,
  // SUB_WORKFLOW. Reads the typed `_containerChildren` wire hint emitted
  // by CanvasJsonMaterializer §5.6 and the legacy `_loopBody` hint.
  applyContainerSizing(nodes, coords, nodeMap, adj);
}

/**
 * §0y: Layer pitch rule.
 * For every vertical transition: y_{n+1} = y_n + h_n + 70
 * where h_n is the height of node n and 70 is the fixed gap.
 *
 * We topologically sort nodes and re-assign y-coordinates layer by layer.
 */
function applyLayerPitch(nodes, coords, nodeMap, adj, radj, defaultNodeHeight) {
  // Compute layer assignment using longest-path
  const layers = new Map();
  const visited = new Set();

  function assignLayerDFS(nodeId) {
    if (visited.has(nodeId)) return layers.get(nodeId) ?? 0;
    visited.add(nodeId);

    const parents = radj.get(nodeId) || [];
    if (parents.length === 0) {
      layers.set(nodeId, 0);
    } else {
      let maxLayer = 0;
      for (const p of parents) {
        const pLayer = assignLayerDFS(p);
        maxLayer = Math.max(maxLayer, pLayer + 1);
      }
      layers.set(nodeId, maxLayer);
    }
    return layers.get(nodeId);
  }

  for (const node of nodes) {
    assignLayerDFS(node.id);
  }

  // Group nodes by layer
  const layerMap = new Map();
  for (const [nodeId, layer] of layers) {
    if (!layerMap.has(layer)) layerMap.set(layer, []);
    layerMap.get(layer).push(nodeId);
  }

  // Compute y-coordinate for each layer top-down
  const layerYs = new Map(); // layer → y-coordinate for that layer's top
  let currentY = 0;

  const sortedLayers = Array.from(layerMap.keys()).sort((a, b) => a - b);
  for (const layer of sortedLayers) {
    layerYs.set(layer, currentY);

    // Find max height of nodes in this layer
    const nodesInLayer = layerMap.get(layer) || [];
    let maxHeight = defaultNodeHeight;
    for (const nodeId of nodesInLayer) {
      const node = nodeMap.get(nodeId);
      // Try to measure content height; fallback to 100
      const h = (node && node.height) ? Math.max(90, Math.min(150, node.height)) : defaultNodeHeight;
      maxHeight = Math.max(maxHeight, h);
    }

    // Advance to next layer: current bottom + 70px gap
    currentY += maxHeight + 70;
  }

  // Apply y-coordinates from layer assignment
  for (const [nodeId, layer] of layers) {
    const currentCoord = coords.get(nodeId);
    if (currentCoord) {
      coords.set(nodeId, { x: currentCoord.x, y: layerYs.get(layer) });
    }
  }
}

/**
 * §0z.1: Compute trunk path — the longest linear chain with no fan-out/fan-in.
 * A node is on the trunk if:
 *   - It has exactly 1 parent (or 0 for start node)
 *   - It has exactly 1 child (or 0 for end node)
 *   - It is not a TERMINATE node (dead-end)
 *
 * Returns a Set of node IDs on the trunk.
 */
function computeTrunkPath(nodes, adj, radj) {
  const trunkSet = new Set();

  // Identify nodes with single in-degree and single out-degree
  for (const node of nodes) {
    // Skip TERMINATE nodes — they are dead-ends
    if (node.type === 'terminate') continue;

    const inDeg = (radj.get(node.id) || []).length;
    const outDeg = (adj.get(node.id) || []).length;

    // Trunk nodes have either:
    // - 0 in-degree, 1 out-degree (start)
    // - 1 in-degree, 1 out-degree (middle)
    // - 1 in-degree, 0 out-degree (end)
    const isStart = inDeg === 0;
    const isEnd = outDeg === 0;
    const isMiddle = inDeg === 1 && outDeg === 1;

    if (isStart || isEnd || isMiddle) {
      trunkSet.add(node.id);
    }
  }

  return trunkSet;
}

/**
 * §0z.1: Apply trunk pinning.
 * Compute the median x-position of all trunk nodes and snap every trunk
 * node to that x-coordinate. This prevents horizontal drift across branches.
 *
 * Returns the computed trunk x-coordinate.
 */
function applyTrunkPinning(nodes, coords, trunkSet) {
  const trunkXs = [];
  for (const nodeId of trunkSet) {
    const c = coords.get(nodeId);
    if (c) trunkXs.push(c.x);
  }

  if (trunkXs.length === 0) return 0; // Fallback if no trunk nodes

  trunkXs.sort((a, b) => a - b);
  const trunkX = trunkXs[Math.floor(trunkXs.length / 2)];

  // Snap all trunk nodes to this x
  for (const nodeId of trunkSet) {
    const c = coords.get(nodeId);
    if (c) {
      coords.set(nodeId, { x: trunkX, y: c.y });
    }
  }

  return trunkX;
}

/**
 * §0z.2: Fork branch splay.
 * For FORK_JOIN and FORK_JOIN_DYNAMIC nodes, position legs symmetrically:
 * leg_x = trunk_x + (i - (N-1)/2) * 278
 * where i is the leg index and N is the number of legs.
 * The middle leg (odd N) sits on trunk.
 */
function applyForkSplay(nodes, coords, adj, trunkX) {
  const FORK_PITCH = 278; // From ORKES_DAG_RULESET.md §S-B

  for (const node of nodes) {
    if (node.type !== 'fork' && node.type !== 'fork_join' &&
        node.type !== 'fork_join_dynamic') continue;

    // Find child nodes (fork legs)
    const children = (adj.get(node.id) || []).filter((c) => {
      // Only direct children; don't follow through dummies or convergences
      const child = nodes.find((n) => n.id === c);
      return child && child.type !== 'join' && child.type !== 'convergence';
    });

    if (children.length < 2) continue; // No splay needed for single leg

    // CRITICAL: Splay around THE FORK'S OWN x, not the global trunk. When a
    // FORK is nested inside a SWITCH case (e.g. stress_test's auto_parallel
    // sitting on the 'auto' branch of claim_type_switch), the fork itself
    // has already been pulled off the global trunk to the switch case's
    // local column. Splaying its children around x=0 would yank them back
    // to the global trunk and, worse, stack them on top of the join/tail
    // nodes that live on the case column. Mirrors the same fix
    // applySwitchSplay uses for nested switches.
    const forkCoord = coords.get(node.id);
    const centerX = (forkCoord && typeof forkCoord.x === 'number') ? forkCoord.x : trunkX;

    // Compute x-positions for each leg relative to the fork's own x
    const N = children.length;
    for (let i = 0; i < N; i++) {
      const offset = (i - (N - 1) / 2) * FORK_PITCH;
      const legX = centerX + offset;

      const coord = coords.get(children[i]);
      if (coord) {
        coords.set(children[i], { x: legX, y: coord.y });
      }
    }
  }
}

/**
 * §S-E1.a: Estimate the horizontal half-width occupied by a single switch case
 * subtree, relative to that case's own column. Used by `applySwitchSplay` to
 * widen the pitch between neighbouring cases whenever a subtree contains a
 * nested fork or switch — without this, a fork nested in a switch case
 * (e.g. `auto_parallel` FORK nested in Claim Type Switch's default case of
 * `complex_claim_stress_test`) splays its legs ±278 around the case column
 * and collides into the adjacent sibling case, which sits only 370px away.
 *
 * Returns the max |offset + NODE_HALF_WIDTH| reachable below `branchRoot`,
 * following the same stopping rules as the switch BFS (stops at
 * convergence/join/terminate/end and at multi-parent nodes).
 */
function computeCaseHalfWidth(branchRoot, nodeMap, adj, radj, seen = new Set()) {
  const NODE_HALF = 165;       // half of standard 330px node box
  const FORK_PITCH = 278;      // mirrors applyForkSplay
  const SWITCH_PITCH = 370;    // mirrors applySwitchSplay base pitch

  if (seen.has(branchRoot)) return NODE_HALF;
  seen.add(branchRoot);

  const node = nodeMap.get(branchRoot);
  if (!node) return NODE_HALF;

  const isStopper = (t) => (
    t === 'convergence' || t === 'join' || t === 'parallel_join' ||
    t === 'switch_join' || t === 'terminate' || t === 'end'
  );
  if (isStopper(node.type)) return NODE_HALF;

  const outgoing = adj.get(branchRoot) || [];
  const liveChildren = outgoing.filter((c) => {
    const ch = nodeMap.get(c);
    if (!ch) return false;
    if (isStopper(ch.type)) return false;
    const parents = radj.get(c) || [];
    // Multi-parent = de-facto convergence; don't descend.
    if (parents.length > 1) return false;
    return true;
  });

  // FORK: children are parallel legs splayed ±FORK_PITCH around the fork.
  if (node.type === 'fork' || node.type === 'fork_join' || node.type === 'fork_join_dynamic') {
    const N = liveChildren.length;
    if (N < 2) {
      // Single leg: recurse linearly.
      let maxHalf = NODE_HALF;
      for (const c of liveChildren) {
        const h = computeCaseHalfWidth(c, nodeMap, adj, radj, seen);
        if (h > maxHalf) maxHalf = h;
      }
      return maxHalf;
    }
    const outerOffset = ((N - 1) / 2) * FORK_PITCH;
    let maxLegHalf = NODE_HALF;
    for (const leg of liveChildren) {
      const h = computeCaseHalfWidth(leg, nodeMap, adj, radj, seen);
      if (h > maxLegHalf) maxLegHalf = h;
    }
    return outerOffset + maxLegHalf;
  }

  // SWITCH nested inside case: children are cases splayed ±SWITCH_PITCH.
  if (node.type === 'switch' || node.type === 'decision' || node.type === 'condition') {
    const N = liveChildren.length;
    if (N < 2) {
      let maxHalf = NODE_HALF;
      for (const c of liveChildren) {
        const h = computeCaseHalfWidth(c, nodeMap, adj, radj, seen);
        if (h > maxHalf) maxHalf = h;
      }
      return maxHalf;
    }
    // Nested switch also gets adaptive pitch recursively — compute per-case
    // half-widths then use cumulative layout to determine outer extent.
    const caseHalves = liveChildren.map((c) =>
      computeCaseHalfWidth(c, nodeMap, adj, radj, new Set(seen)));
    // Position cases cumulatively from left to right.
    const GUTTER = 40;  // matches base SWITCH_PITCH - 2*NODE_HALF = 370 - 330
    const pos = [0];
    for (let i = 1; i < N; i++) {
      const gap = Math.max(SWITCH_PITCH, caseHalves[i - 1] + GUTTER + caseHalves[i]);
      pos.push(pos[i - 1] + gap);
    }
    const leftEdge = pos[0] - caseHalves[0];
    const rightEdge = pos[N - 1] + caseHalves[N - 1];
    // Half-width is max distance from the switch's center (midpoint of
    // leftmost/rightmost case columns).
    const center = (pos[0] + pos[N - 1]) / 2;
    return Math.max(center - leftEdge, rightEdge - center);
  }

  // Linear/default: descend and take max.
  let maxChildHalf = NODE_HALF;
  for (const c of liveChildren) {
    const h = computeCaseHalfWidth(c, nodeMap, adj, radj, seen);
    if (h > maxChildHalf) maxChildHalf = h;
  }
  return maxChildHalf;
}

/**
 * §0z.3: Switch case splay (adaptive pitch).
 * For SWITCH nodes, position branches asymmetrically around the switch's own
 * column (default case leftmost). Pitch between neighbouring cases is
 * `max(SWITCH_PITCH=370, leftHalf + GUTTER + rightHalf)` so that a case
 * containing a nested fork or switch gets its lane widened enough to avoid
 * collision with its siblings (§S-E1.a).
 */
function applySwitchSplay(nodes, coords, adj, radj, trunkX) {
  const SWITCH_PITCH = 370; // From ORKES_DAG_RULESET.md §S-D
  const GUTTER = 40;        // Minimum visual gap (matches SWITCH_PITCH - 2*NODE_HALF = 370 - 330)
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  for (const node of nodes) {
    // CRITICAL: The Conductor→Canvas converter emits SWITCH and DECISION tasks
    // as canvas type `'condition'` (ConductorTaskType.java lines 25-26 both map
    // to "condition"). Previously this splay pass only matched 'switch' /
    // 'decision', so every converted SWITCH skipped splay entirely and its
    // branches collapsed onto the trunk — producing the stacked-nodes bug the
    // user has hit repeatedly on converted workflows.
    if (node.type !== 'switch' && node.type !== 'decision' && node.type !== 'condition') continue;

    // Find case branches (children of the switch before convergence / join).
    // Also exclude explicit join/parallel_join/switch_join since those are
    // convergence-like catch pills that must stay on trunk, not become a branch.
    const children = (adj.get(node.id) || []).filter((c) => {
      const child = nodes.find((n) => n.id === c);
      if (!child) return false;
      const t = child.type;
      return t !== 'convergence' && t !== 'join' && t !== 'parallel_join' && t !== 'switch_join';
    });

    if (children.length < 2) continue;

    // CRITICAL: Splay around THE SWITCH'S OWN x, not the global trunk. Nested
    // switches live on a local branch (e.g. the 'health' branch of an outer
    // Claim Type switch), and their branches must splay around that branch's
    // x — not the global trunk x=0 — or they will be yanked across the canvas
    // into unrelated columns, colliding with sibling subgraphs. Global-trunk
    // splay is only correct for the outermost switch (whose x === trunkX by
    // definition anyway). The stress-test workflow has nested switches nested
    // inside a Claim Type switch nested inside a Validity Switch; splaying
    // everything around trunkX=0 collapses the whole thing into a pile.
    const switchCoord = coords.get(node.id);
    const localTrunkX = (switchCoord && typeof switchCoord.x === 'number') ? switchCoord.x : trunkX;

    const N = children.length; // includes default

    // §S-E1.a: Compute adaptive per-case pitches.
    const caseHalves = children.map((c) =>
      computeCaseHalfWidth(c, nodeMap, adj, radj, new Set()));
    const pos = [0];
    for (let i = 1; i < N; i++) {
      const gap = Math.max(SWITCH_PITCH, caseHalves[i - 1] + GUTTER + caseHalves[i]);
      pos.push(pos[i - 1] + gap);
    }
    // Center the case band around localTrunkX (midpoint of leftmost & rightmost
    // case columns sits on the switch's own x, matching fixed-pitch behaviour
    // when all cases have equal width).
    const center = (pos[0] + pos[N - 1]) / 2;
    const xOffset = localTrunkX - center;

    for (let i = 0; i < N; i++) {
      const caseX = pos[i] + xOffset;

      // BUG #3 FIX: Propagate x-coordinate down the entire branch chain.
      // Only splaying the first node left branches collapsed vertically.
      // CRITICAL: Stop at nodes with multiple parents (de-facto convergence).
      const branchRoot = children[i];
      const visited = new Set();
      const queue = [branchRoot];

      while (queue.length > 0) {
        const currentId = queue.shift();
        if (visited.has(currentId)) continue;
        visited.add(currentId);

        const coord = coords.get(currentId);
        if (coord) {
          coords.set(currentId, { x: caseX, y: coord.y });
        }

        // CRITICAL: If the current node is a FORK, pin the fork itself to
        // caseX (so it sits on the branch's local column) but DO NOT descend
        // into its parallel children. The fork's children are parallel legs
        // — they must be splayed ±278 around the fork by applyForkSplay, not
        // collapsed onto caseX by this BFS. Without this guard, both legs
        // of a nested fork end up at the same (caseX, y) — the exact
        // collision observed in complex_claim_stress_test (task #120).
        const currentNode = nodeMap.get(currentId);
        if (currentNode && (
          currentNode.type === 'fork' ||
          currentNode.type === 'fork_join' ||
          currentNode.type === 'fork_join_dynamic'
        )) {
          continue;
        }

        // Follow chain down, stopping at:
        // - explicit convergence/join/terminate nodes
        // - nodes with multiple parents (de-facto convergence from other branches)
        const childIds = adj.get(currentId) || [];
        for (const childId of childIds) {
          const childNode = nodeMap.get(childId);
          const childParents = radj.get(childId) || [];
          // Stop if: explicit convergence type OR has multiple parents OR is terminate/join/end
          if (childNode && (
            childNode.type === 'convergence' ||
            childNode.type === 'join' ||
            childNode.type === 'terminate' ||
            childNode.type === 'end' ||
            childParents.length > 1  // Multi-parent = de-facto convergence
          )) {
            continue;
          }
          queue.push(childId);
        }
      }
    }
  }
}

/**
 * §0z.4: Catch pill positioning.
 * For convergence nodes (catch pills), position at the x-centroid of
 * live (non-TERMINATE) branches converging into them.
 *
 * A branch is "live" if its final node is not a TERMINATE.
 */
function applyCatchPillPositioning(nodes, coords, nodeMap, adj, radj) {
  for (const node of nodes) {
    if (node.type !== 'convergence') continue;

    // Find all parents (incoming branches)
    const parents = (radj.get(node.id) || []).filter((p) => nodeMap.has(p));

    // Identify live (non-TERMINATE) parents
    const liveParents = parents.filter((p) => {
      const pNode = nodeMap.get(p);
      return pNode && pNode.type !== 'terminate';
    });

    if (liveParents.length === 0) continue;

    // Compute x-centroid of live parents
    let sumX = 0;
    for (const p of liveParents) {
      const pCoord = coords.get(p);
      if (pCoord) sumX += pCoord.x;
    }
    const pillX = sumX / liveParents.length;

    // Apply to convergence node
    const current = coords.get(node.id);
    if (current) {
      coords.set(node.id, { x: pillX, y: current.y });
    }
  }
}

/**
 * §0z.5: Container sizing (#141 — generalized from DO_WHILE-only to all
 * five container kinds).
 *
 * For every container node (SWITCH / FORK_JOIN / FORK_JOIN_DYNAMIC /
 * DO_WHILE / SUB_WORKFLOW), resolve its interior members and terminus,
 * compute the union bbox of the header + every interior member + the
 * terminus (convergence pill / JOIN node), add Conductor OSS-spec padding
 * (50px horizontal, 30px bottom, 12px top-header gap), and publish the
 * result on `node._containerBounds = {x, y, width, height}`.
 *
 * CanvasPage.jsx reads `_containerBounds` and paints a filled tinted
 * rounded rectangle beneath the container's header and body so the
 * group visually wraps its children — matching the Conductor OSS gold standard.
 *
 * Sources of interior membership, in priority order:
 *   1. The typed `_containerChildren` wire hint emitted by
 *      CanvasJsonMaterializer §5.6 (kinds: switch / fork / dynamicFork /
 *      doWhile / subWorkflow). See utils/ir/containerHint.js for shape.
 *   2. The legacy flat `_loopBody` hint (DO_WHILE back-compat path).
 *   3. Loopback-based BFS (DO_WHILE fallback when both hints are empty —
 *      still exercised by unit tests that include an explicit body→loop
 *      edge).
 *
 * After the bbox is computed the function OVERWRITES
 * `node._containerChildren` with a flat array of resolved child ids
 * (post-layout use-site contract, preserved from #122). The structured
 * wire hint only matters until this point; downstream consumers read
 * the flat array.
 *
 * Defaults assume Conductor OSS spec node sizing (W=330, H=100) when per-node
 * dimensions are absent from the flat graph.
 */
function applyContainerSizing(nodes, coords, nodeMap, adj) {
  const NODE_W = 330;
  const NODE_H = 100;
  const PAD_X = 50;           // horizontal padding around contents
  const PAD_BOTTOM = 30;      // bottom padding
  const HEADER_TOP_GAP = 12;  // padding above the container header

  for (const node of nodes) {
    const kind = getContainerKind(node);
    if (!kind) continue;

    const selfCoord = coords.get(node.id);
    if (!selfCoord) continue;

    // Resolve interior child ids + terminus (join/convergence). Terminus
    // nodes are INCLUDED in the bbox calculation because on the Conductor OSS
    // canvas the join / convergence pill sits at the bottom inside the
    // container frame — wrapping it matches the gold standard.
    const { bodyIds, terminusId } = resolveContainerMembers(node, kind, nodeMap, adj);

    // Compute union bbox: include the container header itself + every
    // interior member + terminus (if any).
    const getBox = (id, coord) => {
      const n = nodeMap.get(id);
      const w = (n && n.width) || NODE_W;
      const h = (n && n.height) || NODE_H;
      return { x: coord.x - w / 2, y: coord.y, w, h };
    };

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const selfBox = getBox(node.id, selfCoord);
    minX = selfBox.x; minY = selfBox.y;
    maxX = selfBox.x + selfBox.w; maxY = selfBox.y + selfBox.h;

    const expand = (id) => {
      const c = coords.get(id);
      if (!c) return;
      const b = getBox(id, c);
      if (b.x < minX) minX = b.x;
      if (b.y < minY) minY = b.y;
      if (b.x + b.w > maxX) maxX = b.x + b.w;
      if (b.y + b.h > maxY) maxY = b.y + b.h;
    };
    for (const id of bodyIds) expand(id);
    if (terminusId && !bodyIds.has(terminusId)) expand(terminusId);

    // Apply padding.
    const containerBounds = {
      x: minX - PAD_X,
      y: minY - HEADER_TOP_GAP,
      width: (maxX - minX) + 2 * PAD_X,
      height: (maxY - minY) + HEADER_TOP_GAP + PAD_BOTTOM,
    };

    // RC-17 (task #197) — Publish container metadata for the renderer.
    // Pre-RC-17 this code also wrote a `_orkesRuleMeta.applyDynamicFrame`
    // flag onto every container node "for any consumers still looking at
    // it" — that flag had zero readers in the codebase, was pure brand-
    // leaking cruft, and persisted into workflow JSON on every save.
    // Removed. New saves are clean; legacy persisted blobs still carry the
    // field as inert ballast (operators can re-save to drop it).
    node._containerBounds = containerBounds;
    node._containerKind = kind; // 'switch' | 'fork' | 'dynamicFork' | 'doWhile' | 'subWorkflow'
    node._containerChildren = Array.from(bodyIds);
    if (terminusId) node._containerTerminusId = terminusId;
  }
}

/**
 * Classify a node's container kind, taking both the canvas-type string
 * and any typed wire hint into account. Returns `null` for leaf nodes.
 *
 * Return values mirror {@code CONTAINER_HINT_KIND} in
 * utils/ir/containerHint.js.
 */
function getContainerKind(node) {
  if (!node) return null;
  // Prefer the wire hint when present — it's authoritative (emitted by
  // the backend converter from typed IR ControlBlock data).
  const hint = node._containerChildren;
  if (hint && typeof hint === 'object' && !Array.isArray(hint)
      && typeof hint.kind === 'string') {
    switch (hint.kind) {
      case 'switch': return 'switch';
      case 'fork': return 'fork';
      case 'dynamicFork': return 'dynamicFork';
      case 'doWhile': return 'doWhile';
      case 'subWorkflow': return 'subWorkflow';
      default: break;
    }
  }
  // Fallback: canvas-type sniff. Keeps the unit-test fixtures (which omit
  // the wire hint) and legacy JSON paths working.
  switch (node.type) {
    case 'do_while':
    case 'for_each':
    case 'loop':
      return 'doWhile';
    case 'fork':
    case 'fork_join':
    case 'fork_join_dynamic':
    case 'parallel_fork':
      return 'fork';
    case 'switch':
    case 'decision':
    case 'condition':
      return 'switch';
    case 'sub_workflow':
    case 'async_sub_workflow':
    case 'start_workflow':
      return 'subWorkflow';
    default:
      return null;
  }
}

/**
 * Resolve a container node's interior child ids and its terminus
 * (join / convergence) id. Reads the structured wire hint when present,
 * falls back to `_loopBody` (DO_WHILE) and finally to a loopback BFS
 * for legacy DO_WHILE unit-test fixtures.
 *
 * Returns `{ bodyIds: Set<string>, terminusId: string|null }`.
 */
function resolveContainerMembers(node, kind, nodeMap, adj) {
  const bodyIds = new Set();
  let terminusId = null;

  const hint = node._containerChildren;
  const hasStructuredHint = (
    hint && typeof hint === 'object' && !Array.isArray(hint)
    && typeof hint.kind === 'string'
  );

  if (hasStructuredHint) {
    // Structured wire hint (R3 — task #138 / CanvasJsonMaterializer §5.6).
    switch (hint.kind) {
      case 'switch': {
        if (hint.casePorts && typeof hint.casePorts === 'object') {
          for (const port of Object.keys(hint.casePorts)) {
            const list = hint.casePorts[port];
            if (Array.isArray(list)) {
              for (const id of list) {
                if (typeof id === 'string' && id && nodeMap.has(id) && id !== node.id) {
                  bodyIds.add(id);
                }
              }
            }
          }
        }
        if (typeof hint.convergenceId === 'string' && nodeMap.has(hint.convergenceId)) {
          terminusId = hint.convergenceId;
        }
        break;
      }
      case 'fork': {
        if (Array.isArray(hint.branches)) {
          for (const branch of hint.branches) {
            if (branch && Array.isArray(branch.childIds)) {
              for (const id of branch.childIds) {
                if (typeof id === 'string' && id && nodeMap.has(id) && id !== node.id) {
                  bodyIds.add(id);
                }
              }
            }
          }
        }
        if (typeof hint.joinId === 'string' && nodeMap.has(hint.joinId)) {
          terminusId = hint.joinId;
        }
        break;
      }
      case 'dynamicFork': {
        if (Array.isArray(hint.resolvedBranches)) {
          for (const branch of hint.resolvedBranches) {
            if (branch && Array.isArray(branch.childIds)) {
              for (const id of branch.childIds) {
                if (typeof id === 'string' && id && nodeMap.has(id) && id !== node.id) {
                  bodyIds.add(id);
                }
              }
            }
          }
        }
        if (typeof hint.joinId === 'string' && nodeMap.has(hint.joinId)) {
          terminusId = hint.joinId;
        }
        break;
      }
      case 'doWhile': {
        if (Array.isArray(hint.bodyIds)) {
          for (const id of hint.bodyIds) {
            if (typeof id === 'string' && id && nodeMap.has(id) && id !== node.id) {
              bodyIds.add(id);
            }
          }
        }
        break;
      }
      case 'subWorkflow': {
        if (Array.isArray(hint.embeddedIds)) {
          for (const id of hint.embeddedIds) {
            if (typeof id === 'string' && id && nodeMap.has(id) && id !== node.id) {
              bodyIds.add(id);
            }
          }
        }
        break;
      }
      default:
        break;
    }
    return { bodyIds, terminusId };
  }

  // No structured hint — DO_WHILE legacy paths.
  if (kind === 'doWhile') {
    if (Array.isArray(node._loopBody) && node._loopBody.length > 0) {
      for (const id of node._loopBody) {
        if (nodeMap.has(id) && id !== node.id) bodyIds.add(id);
      }
      return { bodyIds, terminusId };
    }

    // Loopback BFS fallback: a node X is in the body iff it is reachable
    // from the DO_WHILE (downward) AND can reach the DO_WHILE again via
    // a later loopback edge. Only exercised by unit tests that still
    // include an explicit loopback — the converter suppresses it.
    const forwardReach = new Set();
    {
      const stack = [...(adj.get(node.id) || []).filter((id) => id !== node.id)];
      while (stack.length) {
        const id = stack.pop();
        if (!id || forwardReach.has(id) || id === node.id) continue;
        forwardReach.add(id);
        for (const next of adj.get(id) || []) {
          if (next === node.id) continue;
          if (!forwardReach.has(next)) stack.push(next);
        }
      }
    }
    for (const candidate of forwardReach) {
      const stack = [candidate];
      const seen = new Set([candidate]);
      let reachesLoop = false;
      while (stack.length) {
        const id = stack.pop();
        for (const next of adj.get(id) || []) {
          if (next === node.id) { reachesLoop = true; break; }
          if (!seen.has(next)) { seen.add(next); stack.push(next); }
        }
        if (reachesLoop) break;
      }
      if (reachesLoop) bodyIds.add(candidate);
    }
  }

  // For SWITCH / FORK / DYNAMIC_FORK / SUB_WORKFLOW without a wire hint,
  // we have no reliable way to delimit container membership from the
  // flat graph (the whole downstream subtree is reachable), so we leave
  // bodyIds empty — the renderer will fall back to the legacy dashed
  // outline around the header alone. This only happens in hand-authored
  // test fixtures; real converter output ALWAYS carries the hint.
  return { bodyIds, terminusId };
}

// ═══════════════════════════════════════════════════════════════════════════
// POST-LAYOUT PASSES (Bug #4, #6)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * P3-A Bug #4: Anchor comment-marker nodes (// Marks end of switch X)
 * to their parent switch's x coordinate and position below the switch's cases.
 *
 * @param {Array} nodes - positioned nodes from layoutDAG
 * @param {Array} connections - the connections
 * @returns {Array} - nodes with comment markers repositioned
 */
export function anchorCommentMarkers(nodes, connections) {
  if (!nodes || nodes.length === 0) return nodes;

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const adj = new Map();
  const radj = new Map();
  nodes.forEach((n) => { adj.set(n.id, []); radj.set(n.id, []); });
  for (const c of connections) {
    if (nodeMap.has(c.from) && nodeMap.has(c.to)) {
      adj.get(c.from).push(c.to);
      radj.get(c.to).push(c.from);
    }
  }

  const result = nodes.map((n) => ({ ...n }));
  const MARKER_GAP = 50; // pixels below the deepest leg
  const NODE_H = 100;

  for (const node of result) {
    // P3-A Bug #4: Look for comment-style marker nodes
    // "Marks end of switch_X" or similar pattern in the label
    if (!node.label || !node.label.toLowerCase().includes('marks end of')) continue;

    // Extract the referenced switch ID from the label
    // e.g., "// Marks end of switch_1" → look for "switch_1"
    const match = node.label.match(/marks end of\s+(\w+)/i);
    if (!match) continue;

    const switchRefId = match[1];
    const switchNode = nodes.find((n) => n.id && n.id.includes(switchRefId));
    if (!switchNode) continue;

    // §121 slice-2: descend through EACH case's full subtree, not just direct
    // children. For the stress_test, the `auto` case has a nested fork that
    // produces 3 layers of descendants; the old `max(directChild.y) + 100`
    // placed the marker above those deeper legs.
    //
    // Collect every descendant reachable from the switch that still reaches
    // BACK to this marker node (so we don't wander into nodes that escape
    // the switch entirely — those are siblings, not case descendants).
    const caseRootIds = (adj.get(switchNode.id) || [])
      .filter((id) => {
        const n = nodeMap.get(id);
        return n && n.type !== 'convergence' && n.type !== 'join' && n.id !== node.id;
      });

    if (caseRootIds.length === 0) continue;

    // Forward BFS from each case root, stopping at the marker itself.
    const subtreeIds = new Set();
    const queue = [...caseRootIds];
    while (queue.length) {
      const cur = queue.shift();
      if (subtreeIds.has(cur)) continue;
      if (cur === node.id) continue; // never count the marker itself
      subtreeIds.add(cur);
      const next = adj.get(cur) || [];
      for (const nxt of next) {
        if (nxt === node.id) continue;
        if (!subtreeIds.has(nxt)) queue.push(nxt);
      }
    }

    // Filter to descendants that actually reach the marker (excludes nodes
    // that escape the switch via some other path).
    const reaches = new Map();
    const reachesMarker = (id) => {
      if (reaches.has(id)) return reaches.get(id);
      if (id === node.id) return true;
      reaches.set(id, false); // prevent cycles
      const next = adj.get(id) || [];
      for (const nxt of next) {
        if (reachesMarker(nxt)) { reaches.set(id, true); return true; }
      }
      return false;
    };

    const legNodes = Array.from(subtreeIds)
      .map((id) => nodeMap.get(id))
      .filter((n) => n && reachesMarker(n.id));

    // If no descendants reach the marker (shouldn't happen in a well-formed
    // switch), fall back to direct children so the marker still anchors.
    const anchorNodes = legNodes.length > 0
      ? legNodes
      : caseRootIds.map((id) => nodeMap.get(id)).filter(Boolean);

    if (anchorNodes.length === 0) continue;

    // Position the marker at:
    // x = switch's x-coordinate (centers under the fan-out)
    // y = deepest leg bottom + MARKER_GAP
    const markerX = switchNode.x;
    const deepestY = Math.max(...anchorNodes.map((c) => c.y + NODE_H));
    const markerY = deepestY + MARKER_GAP;

    const idx = result.findIndex((n) => n.id === node.id);
    if (idx >= 0) {
      result[idx] = { ...result[idx], x: markerX, y: markerY };
    }
  }

  return result;
}

/**
 * P3-A Bug #6: Align JOIN-inbound branches to the same layer Y.
 * Branches that reconverge at a JOIN should have all their endpoints
 * at the same y-coordinate for clean vertical alignment.
 *
 * @param {Array} nodes - positioned nodes
 * @param {Array} connections - the connections
 * @returns {Array} - nodes with JOIN-inbound branches aligned
 */
export function alignJoinInboundBranches(nodes, connections) {
  if (!nodes || nodes.length === 0) return nodes;

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const radj = new Map();
  nodes.forEach((n) => { radj.set(n.id, []); });
  for (const c of connections) {
    if (nodeMap.has(c.from) && nodeMap.has(c.to)) {
      radj.get(c.to).push(c.from);
    }
  }

  const result = nodes.map((n) => ({ ...n }));

  // Find all JOIN nodes
  for (const node of result) {
    if (node.type !== 'join' && node.type !== 'convergence') continue;

    const inboundSources = (radj.get(node.id) || [])
      .map((id) => nodeMap.get(id))
      .filter((n) => n);

    if (inboundSources.length < 2) continue; // No alignment needed for single source

    // Compute the maximum y among all inbound sources
    const maxSourceY = Math.max(...inboundSources.map((s) => s.y));

    // P3-A Bug #6: Set all inbound sources to the same y (the max)
    // This ensures they all reach the JOIN at the same vertical level.
    for (const source of inboundSources) {
      const idx = result.findIndex((n) => n.id === source.id);
      if (idx >= 0) {
        result[idx] = { ...result[idx], y: maxSourceY };
      }
    }
  }

  return result;
}

/**
 * §121e Gap C: Re-pin JOIN / SWITCH_JOIN / convergence nodes to their
 * originating FORK / SWITCH's x after Sugiyama coord-assignment. When a
 * fan-out has legs of uneven depth or width, Sugiyama's barycenter method
 * tilts the convergence x toward the heavier subtree — the JOIN drifts
 * off-trunk and the whole workflow looks bent. Conductor OSS pins the JOIN
 * precisely under the splitting node, so the trunk stays straight.
 *
 * Algorithm:
 *  - For each JOIN / convergence node, BFS backward (via radj) until we
 *    hit a FORK / SWITCH ancestor that reaches this join on EVERY path
 *    (i.e. the matching splitter).
 *  - Set join.x := splitter.x.
 *
 * The "every path" constraint avoids mis-pinning when a join's inbound
 * branches originate from disjoint splitters (rare but possible in nested
 * shapes).
 *
 * @param {Array} nodes - positioned nodes
 * @param {Array} connections - the connections
 * @returns {Array} - nodes with JOIN x re-pinned to matching splitter
 */
export function pinJoinsToSplitterTrunk(nodes, connections) {
  if (!nodes || nodes.length === 0) return nodes;

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const radj = new Map();
  const adj = new Map();
  nodes.forEach((n) => { radj.set(n.id, []); adj.set(n.id, []); });
  for (const c of connections) {
    if (nodeMap.has(c.from) && nodeMap.has(c.to)) {
      radj.get(c.to).push(c.from);
      adj.get(c.from).push(c.to);
    }
  }

  const JOIN_TYPES = new Set(['join', 'parallel_join', 'switch_join', 'convergence']);
  const SPLITTER_TYPES = new Set(['fork', 'fork_join', 'dynamic_fork', 'switch', 'decision', 'condition']);

  const result = nodes.map((n) => ({ ...n }));

  for (const joinNode of result) {
    if (!JOIN_TYPES.has(joinNode.type)) continue;

    // BFS backward, collecting all reachable ancestors. For each splitter
    // ancestor, check: does every forward path from splitter reach joinNode?
    const ancestors = new Set();
    const queue = [joinNode.id];
    while (queue.length) {
      const cur = queue.shift();
      if (ancestors.has(cur)) continue;
      ancestors.add(cur);
      for (const p of (radj.get(cur) || [])) {
        if (!ancestors.has(p)) queue.push(p);
      }
    }

    // Candidate splitters: ancestors that are fork/switch types.
    const candidates = Array.from(ancestors)
      .map((id) => nodeMap.get(id))
      .filter((n) => n && SPLITTER_TYPES.has(n.type) && n.id !== joinNode.id);

    if (candidates.length === 0) continue;

    // Pick the splitter whose ALL direct children can reach joinNode.
    // (The matching splitter's every branch converges at this join.)
    const canReach = new Map();
    const canReachJoin = (id) => {
      if (canReach.has(id)) return canReach.get(id);
      if (id === joinNode.id) return true;
      canReach.set(id, false); // cycle guard
      const next = adj.get(id) || [];
      for (const nxt of next) {
        if (canReachJoin(nxt)) { canReach.set(id, true); return true; }
      }
      return false;
    };

    let matched = null;
    // Pick the DEEPEST matching splitter (closest to the join), so in
    // nested shapes the inner splitter wins over the outer.
    for (const cand of candidates) {
      const children = adj.get(cand.id) || [];
      if (children.length < 2) continue;
      const allReach = children.every((c) => canReachJoin(c));
      if (!allReach) continue;
      if (!matched || (cand.y || 0) > (matched.y || 0)) {
        matched = cand;
      }
    }

    if (!matched) continue;

    const idx = result.findIndex((n) => n.id === joinNode.id);
    if (idx >= 0 && typeof matched.x === 'number') {
      result[idx] = { ...result[idx], x: matched.x };
    }
  }

  return result;
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

function computeLayerCounts(nodes, connections) {
  if (nodes.length === 0) return [];

  const nodeSet = new Set(nodes.map((n) => n.id));
  const adj = new Map();
  const radj = new Map();
  nodeSet.forEach((id) => { adj.set(id, []); radj.set(id, []); });

  for (const c of connections) {
    if (nodeSet.has(c.from) && nodeSet.has(c.to)) {
      adj.get(c.from).push(c.to);
      radj.get(c.to).push(c.from);
    }
  }

  // Topological sort
  const inDeg = new Map();
  nodeSet.forEach((id) => inDeg.set(id, (radj.get(id) || []).length));
  const queue = [];
  for (const [id, d] of inDeg) {
    if (d === 0) queue.push(id);
  }
  const topo = [];
  while (queue.length > 0) {
    const n = queue.shift();
    topo.push(n);
    for (const next of (adj.get(n) || [])) {
      inDeg.set(next, inDeg.get(next) - 1);
      if (inDeg.get(next) === 0) queue.push(next);
    }
  }

  // Longest path layer assignment
  const layers = new Map();
  for (const n of topo) {
    const parents = radj.get(n) || [];
    let maxL = 0;
    for (const p of parents) {
      if (layers.has(p)) maxL = Math.max(maxL, layers.get(p) + 1);
    }
    layers.set(n, maxL);
  }

  // Count per layer
  const counts = new Map();
  for (const [, l] of layers) {
    counts.set(l, (counts.get(l) || 0) + 1);
  }

  const maxLayer = Math.max(...counts.keys(), 0);
  const result = [];
  for (let i = 0; i <= maxLayer; i++) {
    result.push(counts.get(i) || 0);
  }
  return result;
}
